import { useEffect, useState } from "react";
import styles from "./Document.module.css";
import FilterDropdown from './FilterDropdown';

const apiBase = process.env.REACT_APP_DOCUMENT_API_BASE;

export function DocumentUploader() {
  const [file, setFile] = useState(null);
  const [userId, setUserId] = useState("");
  const [vehicleId, setVehicleId] = useState("VEH001");
  const [vehicleNumber, setVehicleNumber] = useState("MH12AB1234");
  const [documentType, setDocumentType] = useState("INSURANCE");
  const [issueDate, setIssueDate] = useState("2026-01-01");
  const [expiryDate, setExpiryDate] = useState("2027-01-01");
  const [uploadStatus, setUploadStatus] = useState("");
  const [error, setError] = useState("");
  const [responseData, setResponseData] = useState(null);

  const [filterVehicleId, setFilterVehicleId] = useState("");
  const [filterDocumentType, setFilterDocumentType] = useState("");
  const [filterExpiryYear, setFilterExpiryYear] = useState("");
  const [filterDocumentStatus] = useState("");
  const [searchMessage, setSearchMessage] = useState("");
  const [filterError, setFilterError] = useState("");
  const [filterResults, setFilterResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showUploadForm, setShowUploadForm] = useState(false);

  useEffect(() => {
    const authUser = typeof window !== "undefined" ? localStorage.getItem("authUser") : "";
    const currentUserId = authUser || "USR001";
    setUserId(currentUserId);
    fetchUserDocuments(currentUserId);
  }, []);

  const fetchUserDocuments = async (uid) => {
    setIsLoading(true);
    setFilterError("");
    try {
      // Trying different parameter variations to ensure we get data
      const baseUrl = `${apiBase}/documents`;
      const urlsToTry = [
        `${baseUrl}?userId=${encodeURIComponent(uid)}`,
        `${baseUrl}?userId=${uid}`, // Try without encoding just in case
        baseUrl // Try without userId if the above fail (not recommended for production)
      ];

      let lastError = null;
      for (const url of urlsToTry) {
        try {
          const response = await fetch(url);
          if (response.ok) {
            const data = await response.json();
            const items = data.items || data.documents || (Array.isArray(data) ? data : []);
            if (items.length > 0 || url === urlsToTry[0]) {
              setFilterResults(items);
              return; // Success!
            }
          }
        } catch (e) {
          lastError = e;
        }
      }

      if (lastError) throw lastError;
      setFilterResults([]);
    } catch (err) {
      console.error("Error fetching documents:", err);
      setFilterError("Could not load documents. Please check your connection.");
    } finally {
      setIsLoading(false);
    }
  };

  const resetState = () => {
    setUploadStatus("");
    setError("");
    setResponseData(null);
  };

  const handleFileChange = (event) => {
    resetState();
    setFile(event.target.files[0] || null);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    resetState();

    if (!file) {
      setError("Please select a PDF or image file before uploading.");
      return;
    }

    try {
      setUploadStatus("Creating upload metadata...");

      const initResponse = await fetch(`${apiBase}/upload`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userId,
          vehicleId,
          vehicleNumber,
          documentType,
          issueDate,
          expiryDate,
          fileName: file.name,
          mimeType: file.type || "application/pdf",
          fileSize: file.size,
        }),
      });

      if (!initResponse.ok) {
        const errorBody = await initResponse.text();
        throw new Error(`Upload init failed: ${initResponse.status} ${errorBody}`);
      }

      const initResult = await initResponse.json();
      setResponseData(initResult);

      const uploadResponse = await fetch(initResult.presignedUrl, {
        method: "PUT",
        headers: { "Content-Type": file.type || "application/pdf" },
        body: file,
      });

      if (!uploadResponse.ok) {
        const uploadText = await uploadResponse.text();
        throw new Error(`S3 upload failed: ${uploadResponse.status} ${uploadText}`);
      }

      setUploadStatus("Processing complete! Document uploaded.");
      setResponseData(initResult);

      // Refresh the document list immediately
      fetchUserDocuments(userId);

      // Keep the success message visible for a moment then close
      setTimeout(() => {
        setShowUploadForm(false);
        setUploadStatus("");
        resetState();
      }, 2500);
    } catch (err) {
      console.error("Upload error:", err);
      setError(`Upload failed: ${err.message}`);
      setUploadStatus("");
    }
  };

  const handleSearch = async () => {
    setFilterError("");
    setSearchMessage("Searching...");
    setIsLoading(true);

    const params = new URLSearchParams();
    params.append("userId", userId);
    if (filterVehicleId) params.append("vehicleId", filterVehicleId);
    if (filterDocumentType) params.append("documentType", filterDocumentType);
    if (filterExpiryYear) params.append("expiryYear", filterExpiryYear);
    if (filterDocumentStatus) params.append("status", filterDocumentStatus);

    try {
      const response = await fetch(`${apiBase}/documents?${params.toString()}`);
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Search failed: ${response.status} ${errorText}`);
      }

      const data = await response.json();
      const items = data.items || data.documents || (Array.isArray(data) ? data : []);
      setFilterResults(items);
      setSearchMessage(`Found ${items.length} document(s)`);
    } catch (err) {
      setFilterError(err.message);
      setSearchMessage("");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={styles.documentUploader}>
      <div className={styles.docHeader}>
        <div className={styles.headerActions}>
          <div className={styles.filterBar}>
            <input
              value={filterVehicleId}
              onChange={(e) => setFilterVehicleId(e.target.value)}
              placeholder="Vehicle ID"
              className={styles.compactInput}
            />
           

            <input
              value={filterExpiryYear}
              onChange={(e) => setFilterExpiryYear(e.target.value)}
              placeholder="Expiry Year"
              className={`${styles.compactInput} ${styles.yearInput}`}
            />
             <FilterDropdown
              value={filterDocumentType}
              onChange={setFilterDocumentType}
              options={[
                { value: 'INSURANCE', label: 'INSURANCE' },
                { value: 'TAX_TOKEN', label: 'TAX_TOKEN' },
                { value: 'REGISTRATION', label: 'REGISTRATION' },
                { value: 'PERMIT', label: 'PERMIT' }
              ]}
            />
            <button type="button" className={styles.searchBtn} onClick={handleSearch}>
              Filter
            </button>
          </div>
          <div style={{ marginTop: 8, minHeight: 20 }}>
            {filterError && <span style={{ color: '#c53030' }}>{filterError}</span>}
            {!filterError && searchMessage && <span style={{ color: '#374151' }}>{searchMessage}</span>}
          </div>
          <button
            type="button"
            className={styles.uploadTriggerBtn}
            onClick={() => setShowUploadForm(true)}
          >
            Upload Document
          </button>
        </div>
      </div>

      <div className={styles.contentPanel}>
        {isLoading ? (
          <div className={styles.loadingState}>Loading documents...</div>
        ) : (
          <div className={styles.documentsGrid}>
            {filterResults.length > 0 ? (
              filterResults.map((item, index) => (
                <div key={item.documentId || index} className={styles.docCard}>
                  <div className={styles.docCardHeader}>
                    <div className={styles.badgeGroup}>
                      <span className={`${styles.statusBadge} ${item.documentStatus?.toLowerCase()}`}>
                        {item.documentStatus}
                      </span>
                      {item.ocrStatus && (
                        <span className={`${styles.ocrBadge} ${item.ocrStatus?.toLowerCase()}`}>
                          OCR: {item.ocrStatus}
                        </span>
                      )}
                    </div>
                    <h3>{item.documentType}</h3>
                  </div>
                  <div className={styles.docCardBody}>
                    <div className={styles.infoRow}>
                      <strong>ID:</strong> <span>{item.documentId}</span>
                    </div>
                    <div className={styles.infoRow}>
                      <strong>Vehicle:</strong> <span>{item.vehicleNumber || item.vehicleId}</span>
                    </div>
                    <div className={styles.infoRow}>
                      <strong>Uploaded:</strong> <span>{item.uploadedAt ? new Date(item.uploadedAt).toLocaleDateString() : 'N/A'}</span>
                    </div>
                    <div className={styles.infoRow}>
                      <strong>Expiry:</strong> <span className={item.extractedData?.expiryDate && item.expiryDate !== item.extractedData.expiryDate ? styles.mismatch : ""}>{item.expiryDate}</span>
                    </div>
                    <div className={`${styles.infoRow} ${styles.fileInfo}`}>
                      <strong>File:</strong> <span>{item.fileName || item.s3Key?.split('/').pop()}</span>
                    </div>
                  </div>

                  {item.extractedData && (
                    <div className={styles.extractedSection}>
                      <h4>Extracted Data</h4>
                      <div className={styles.extractedGrid}>
                        {item.extractedData.insuredName && (
                          <div className={styles.extItem}>
                            <label>Insured</label>
                            <span>{item.extractedData.insuredName}</span>
                          </div>
                        )}
                        {item.extractedData.policyNumber && (
                          <div className={styles.extItem}>
                            <label>Policy #</label>
                            <span>{item.extractedData.policyNumber}</span>
                          </div>
                        )}
                        {item.extractedData.expiryDate && (
                          <div className={styles.extItem}>
                            <label>OCR Expiry</label>
                            <span className={item.expiryDate !== item.extractedData.expiryDate ? styles.warning : ""}>
                              {item.extractedData.expiryDate}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className={styles.emptyMessage}></div>
            )}
          </div>
        )}
      </div>

      {showUploadForm && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <div className={styles.modalHeader}>
              <h2>Upload New Document</h2>
              <button className={styles.closeBtn} onClick={() => setShowUploadForm(false)}>&times;</button>
            </div>

            <form className={styles.uploadForm} onSubmit={handleSubmit}>
              <div className={styles.formRow}>
                <label>Vehicle ID</label>
                <input value={vehicleId} onChange={(e) => setVehicleId(e.target.value)} required />
              </div>

              <div className={styles.formRow}>
                <label>Vehicle Number</label>
                <input value={vehicleNumber} onChange={(e) => setVehicleNumber(e.target.value)} required />
              </div>

              <div className={styles.formRow}>
                <label>Document Type</label>
                <select value={documentType} onChange={(e) => setDocumentType(e.target.value)}>
                  <option value="INSURANCE">INSURANCE</option>
                  <option value="TAX_TOKEN">TAX TOKEN</option>
                  <option value="REGISTRATION">REGISTRATION</option>
                  <option value="PERMIT">PERMIT</option>
                </select>
              </div>

              <div className={styles.formRow}>
                <label>Issue Date</label>
                <input type="date" value={issueDate} onChange={(e) => setIssueDate(e.target.value)} required />
              </div>

              <div className={styles.formRow}>
                <label>Expiry Date</label>
                <input type="date" value={expiryDate} onChange={(e) => setExpiryDate(e.target.value)} required />
              </div>

              <div className={styles.formRow}>
                <label>File</label>
                <input type="file" accept="application/pdf,image/*" onChange={handleFileChange} required />
              </div>

              <div className={styles.modalFooter}>
                <button type="button" className={styles.cancelBtn} onClick={() => setShowUploadForm(false)}>Cancel</button>
                <button type="submit" className={styles.uploadButton}>Upload</button>
              </div>
            </form>

            {uploadStatus && <div className={`${styles.status} ${styles.success}`}>{uploadStatus}</div>}
            {error && <div className={`${styles.status} ${styles.error}`}>{error}</div>}

            {responseData && (
              <div className={styles.uploadSummary}>
                <h3>Upload Success</h3>
                <p>Document ID: {responseData.documentId}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
